import { Position, positionKey } from '../core/GridGeometry.js';
import { GridHitDetector } from '../core/GridHitDetector.js';
import { getCanvasCoordinates } from '../input/canvasCoordinates.js';
import { IVortexGame } from '../games/IVortexGame.js';
import { KeyboardControllable } from '../input/KeyboardController.js';

/** Minimal renderer surface needed by the Activate Vortex controller. */
export interface ActivateRenderer {
    getConfig(): { cellSize: number; padding: number };
    getGridSize(): number;
    setGatePendingCreateCell(pos: Position | null): void;
}

export interface VortexActivateInputOptions {
    /** Create a gate pair in the shared Hub. Returns false if it could not be created. */
    createPair: (a: Position, b: Position) => boolean;
    /** Whether a fresh gate can be placed on a cell (no digit, no gate, not on a link path). */
    canPlaceGate: (pos: Position) => boolean;
}

/**
 * App-owned input controller for in-game "Activate Vortex" mode.
 *
 * Left-click:
 *  - on a gate pair → toggle its activation.
 *  - on a placeable empty cell → start a new pair (first gate pending); a second
 *    placeable cell commits the pair (auto-activated when possible). Re-clicking
 *    the first cell cancels.
 *
 * Right-click: cancel a pending creation.
 */
export class VortexActivateInput implements KeyboardControllable {
    private game: IVortexGame;
    private renderer: ActivateRenderer;
    private canvas: HTMLCanvasElement;
    private detector: GridHitDetector;
    private onUpdate: () => void;
    private createPair: (a: Position, b: Position) => boolean;
    private canPlaceGate: (pos: Position) => boolean;
    private pendingFirst: Position | null = null;

    constructor(
        game: IVortexGame,
        renderer: ActivateRenderer,
        canvas: HTMLCanvasElement,
        onUpdate: () => void,
        options: VortexActivateInputOptions
    ) {
        this.game = game;
        this.renderer = renderer;
        this.canvas = canvas;
        this.onUpdate = onUpdate;
        this.createPair = options.createPair;
        this.canPlaceGate = options.canPlaceGate;

        const config = renderer.getConfig();
        this.detector = new GridHitDetector({
            cellSize: config.cellSize,
            padding: config.padding,
            gridSize: renderer.getGridSize(),
        });

        this.canvas.addEventListener('click', this.handleClick);
        this.canvas.addEventListener('contextmenu', this.handleRightClick);
    }

    private setPending(pos: Position | null): void {
        this.pendingFirst = pos;
        this.renderer.setGatePendingCreateCell(pos);
    }

    private handleClick = (e: MouseEvent): void => {
        const { x, y } = getCanvasCoordinates(this.canvas, e);
        const cell = this.detector.detectCell(x, y);
        if (cell && this.actOnCell(cell)) this.onUpdate();
    };

    /**
     * The click FSM, shared by the mouse and the keyboard: a gate toggles its pair,
     * a placeable cell starts a pair or commits it, and re-picking the first cell
     * cancels. Returns true when the view needs redrawing.
     */
    actOnCell(cell: Position): boolean {
        // Picking an existing gate toggles its pair (and cancels any pending create)
        if (this.game.isGateCell(cell)) {
            if (this.pendingFirst) this.setPending(null);
            this.game.toggleVortexGate(cell);
            return true;
        }

        // Only placeable cells participate in creation
        if (!this.canPlaceGate(cell)) return false;

        if (this.pendingFirst) {
            if (positionKey(cell) === positionKey(this.pendingFirst)) {
                this.setPending(null);   // re-picking the first cell cancels
                return true;
            }
            const first = this.pendingFirst;
            this.setPending(null);
            this.createPair(first, cell);
            return true;
        }

        this.setPending({ ...cell });    // start a new pair
        return true;
    }

    /** Cancel a pending creation (right-click, or `C` in keyboard mode). */
    cancelPending(): boolean {
        if (!this.pendingFirst) return false;
        this.setPending(null);
        return true;
    }

    private handleRightClick = (e: MouseEvent): void => {
        e.preventDefault();
        if (this.cancelPending()) this.onUpdate();
    };

    // ── Keyboard mode ─────────────────────────────────────────────────
    // The same two verbs as the mouse: a tap runs the click FSM, `C` cancels a
    // pending pair. No trace — creating a pair is two cells, and the second one can
    // be anywhere placeable, so a confined gesture would add nothing over two taps.

    placeAtCursor(pos: Position): boolean {
        if (this.actOnCell(pos)) this.onUpdate();
        return false; // the FSM refreshes and saves through onUpdate itself
    }

    deleteAtCursor(_pos: Position): boolean {
        if (this.cancelPending()) this.onUpdate();
        return false;
    }

    /** `X` goes to the first gate pair — the network you're switching on. */
    cursorOrigin(): Position | null {
        const pairs = this.game.getVortexGatePairs();
        return pairs.length > 0 ? { ...pairs[0][0] } : null;
    }

    destroy(): void {
        this.setPending(null);
        this.canvas.removeEventListener('click', this.handleClick);
        this.canvas.removeEventListener('contextmenu', this.handleRightClick);
    }
}
